import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  collection, 
  query, 
  onSnapshot,
  updateDoc,
  doc,
  where,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { motion } from 'framer-motion';

const DriverDashboard = () => {
  const { currentUser, userData } = useAuth();
  const [availableBookings, setAvailableBookings] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('available');
  const [driverStats, setDriverStats] = useState({
    totalApplications: 0,
    acceptedJobs: 0,
    pendingApplications: 0
  });

  useEffect(() => {
    if (!userData?.city) return;

    // Fetch approved bookings in driver's city
    const q = query(
      collection(db, 'bookings'),
      where('status', '==', 'approved'),
      where('city', '==', userData.city)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const bookingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableBookings(bookingsData);
    });

    return () => unsubscribe();
  }, [userData?.city]);

  useEffect(() => {
    if (!currentUser) return;

    // Fetch driver's applications
    const q = query(
      collection(db, 'applications'),
      where('driverId', '==', currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const applicationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMyApplications(applicationsData);
      
      // Update stats
      setDriverStats({
        totalApplications: applicationsData.length,
        acceptedJobs: applicationsData.filter(app => app.status === 'accepted').length,
        pendingApplications: applicationsData.filter(app => app.status === 'pending').length
      });
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleApplyForBooking = async (booking) => {
    try {
      // Create application
      await addDoc(collection(db, 'applications'), {
        bookingId: booking.id,
        driverId: currentUser.uid,
        driverName: `${userData.firstName} ${userData.lastName}`,
        driverPhone: userData.phone,
        driverEmail: currentUser.email,
        status: 'pending',
        appliedAt: serverTimestamp(),
        bookingDetails: {
          carType: booking.carType,
          pickupLocation: booking.pickupLocation,
          destination: booking.destination,
          date: booking.date,
          hours: booking.hours,
          price: booking.price,
          userEmail: booking.userEmail,
          userPhone: booking.userPhone
        }
      });

      // Update booking with driver application notification
    await updateDoc(doc(db, 'bookings', booking.id), {
      'notifications.user': `Driver ${userData.firstName} has applied for your booking. Check your applications to accept.`
    });

    alert('Application submitted successfully! The user will be notified.');
  } catch (error) {
    console.error('Error applying for booking:', error);
    alert('Error submitting application. Please try again.');
  }
};


  const statCards = [
    {
      title: 'Total Applications',
      value: driverStats.totalApplications,
      color: 'blue',
      icon: '📝'
    },
    {
      title: 'Accepted Jobs',
      value: driverStats.acceptedJobs,
      color: 'green',
      icon: '✅'
    },
    {
      title: 'Pending Applications',
      value: driverStats.pendingApplications,
      color: 'yellow',
      icon: '⏳'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">
            Driver Dashboard
          </h1>
          <p className="text-gray-600">
            Welcome, {userData?.firstName}! Available bookings in {userData?.city}
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <div className="flex items-center">
                <div className="text-3xl mr-4">{stat.icon}</div>
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-lg">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'available', name: 'Available Bookings', count: availableBookings.length },
                { id: 'myApplications', name: 'My Applications', count: myApplications.length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name} ({tab.count})
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'available' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-4">
                  Available Bookings in {userData?.city}
                </h3>
                
                {availableBookings.map((booking, index) => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6 bg-blue-50"
                  >
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-xl font-semibold mb-2">
                          {booking.carType} Booking
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="font-medium">Route:</span> {booking.pickupLocation} → {booking.destination}</p>
                          <p><span className="font-medium">Date & Time:</span> {booking.date?.toDate?.() ? new Date(booking.date.toDate()).toLocaleString() : 'Not specified'}</p>
                          <p><span className="font-medium">Duration:</span> {booking.hours} hours</p>
                          <p><span className="font-medium">User:</span> {booking.userEmail}</p>
                          <p><span className="font-medium">User Phone:</span> {booking.userPhone || 'Not provided'}</p>
                        </div>
                      </div>
                      
                      <div>
                        <div className="bg-white p-4 rounded-lg mb-4">
                          <h5 className="font-semibold mb-2">Pricing Details</h5>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span>Subtotal:</span>
                              <span>£{booking.price?.subtotal}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>GST (20%):</span>
                              <span>£{booking.price?.tax}</span>
                            </div>
                            <div className="flex justify-between font-semibold border-t pt-1">
                              <span>Total:</span>
                              <span>Rs{booking.price?.total}</span>
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleApplyForBooking(booking)}
                          className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                        >
                          Apply for this Booking
                        </button>
                      </div>
                    </div>
                    
                    {booking.notifications?.drivers && (
                      <p className="text-sm text-blue-600 mt-3 bg-white p-2 rounded">
                        📢 {booking.notifications.drivers}
                      </p>
                    )}
                  </motion.div>
                ))}
                
                {availableBookings.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🚗</div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      No available bookings
                    </h3>
                    <p className="text-gray-500">
                      There are currently no approved bookings in {userData?.city}. Check back later!
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'myApplications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-4">My Job Applications</h3>
                
                {myApplications.map((application, index) => (
                  <motion.div
                    key={application.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6 bg-white"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-semibold">
                          {application.bookingDetails.carType} - {application.bookingDetails.pickupLocation} to {application.bookingDetails.destination}
                        </h4>
                        <p className="text-gray-600">
                          Applied on {application.appliedAt?.toDate?.() ? new Date(application.appliedAt.toDate()).toLocaleDateString() : 'Recent'}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        application.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {application.status}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">Date:</span> {application.bookingDetails.date?.toDate?.() ? new Date(application.bookingDetails.date.toDate()).toLocaleString() : 'Not specified'}</p>
                        <p><span className="font-medium">Hours:</span> {application.bookingDetails.hours}</p>
                        <p><span className="font-medium">Total Pay:</span> £{application.bookingDetails.price?.total}</p>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">User Email:</span> {application.bookingDetails.userEmail}</p>
                        <p><span className="font-medium">Your Contact:</span> {application.driverPhone}</p>
                        {application.status === 'accepted' && (
                          <div className="bg-green-50 p-3 rounded-lg">
                            <p className="text-green-800 font-semibold">
                              ✅ You've been accepted! Contact the user at: {application.bookingDetails.userEmail}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {myApplications.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📝</div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      No applications yet
                    </h3>
                    <p className="text-gray-500">
                      You haven't applied for any bookings yet. Check the available bookings tab to get started!
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverDashboard;